import { useGetSimpleUsersByRolesQuery } from 'store/services/core/user';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { styled, InputLabel } from '@mui/material';

import { Button } from 'components/mui/common/Button';
import { NameArray } from 'components/common/Table/cellComponents/NameArray';
import { Loader } from 'components/common/Loader';

import { useModals } from 'utils/hooks';
import { UsersFilterModal } from './UsersFilterModal';

const UsersFilterWrapper = styled('div')(() => ({
  width: '100%',
}));

interface UsersFilterProps {
  value?: string | string[];
  onChange: (value: string | string[] | null) => void;
  onBlur: () => void;
  disabled?: boolean;
  label: string;
  roleIds: string[];
}

export const UsersFilter = memo(({ value, onChange, onBlur, disabled, label, roleIds }: UsersFilterProps) => {
  const { t } = useTranslation();
  const [modals, openModal, closeModal] = useModals({
    users_filter: false,
  });

  const { data: { items: users = [] } = {}, isFetching } = useGetSimpleUsersByRolesQuery(
    {
      page: 1,
      pageSize: null,
      orderBy: 'fullName',
      orderDesc: false,
      search: '',
      ids: roleIds,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: roleIds.length === 0,
    }
  );

  const selectedUsers = useMemo(() => {
    if (!value) return [];
    const selectedIds = Array.isArray(value) ? value : [value];
    return users.filter(({ id }) => selectedIds.includes(id)).map(({ id, fullName }) => ({ id, fullName }));
  }, [users, value]);

  const handleClose = () => {
    closeModal('users_filter')();
  };

  const handleSubmit = (selectedUserIds: string[]) => {
    if (selectedUserIds.length === 0) {
      onChange(null);
    } else {
      onChange(selectedUserIds);
    }
    onBlur();
    closeModal('users_filter')();
  };

  const handleEdit = () => {
    openModal('users_filter')();
  };

  if (isFetching)
    return (
      <UsersFilterWrapper>
        <InputLabel>{label}</InputLabel>
        <Loader />
      </UsersFilterWrapper>
    );

  return (
    <>
      <UsersFilterWrapper>
        <InputLabel>{label}</InputLabel>
        <Button type="button" variant="outlined" onClick={openModal('users_filter')} disabled={disabled} fullWidth>
          {selectedUsers.length === 0 ? t('common.select') : <NameArray value={selectedUsers.map(({ fullName }) => fullName)} onEdit={handleEdit} />}
        </Button>
      </UsersFilterWrapper>

      <UsersFilterModal
        title={label}
        open={modals?.users_filter}
        roleIds={roleIds}
        selectedUserIds={Array.isArray(value) ? value : value ? [value] : []}
        onClose={handleClose}
        onSubmit={handleSubmit}
      />
    </>
  );
});
